/**
 * Shared column state with subscriber pattern.
 * Allows middleware to update columns and widgets to react to changes.
 */

let columns = [];
const subscribers = new Set();
const STORAGE_KEY = "chamfr-column-config";
const FIXED_FIRST = "item-number";
const FIXED_LAST = "price";

/**
 * Get current category from URL or search state
 * @returns {string} Category slug (defaults to 'SERP')
 */
function getCurrentCategory() {
  // Check if we're on a supplier store page (check for non-empty data-supplier-name)
  const supplierStoreElement = document.querySelector("[data-supplier-name]");
  if (supplierStoreElement && supplierStoreElement.dataset.supplierName && supplierStoreElement.dataset.supplierName.trim() !== "") {
    if (supplierStoreElement.dataset.activeCategorySlug && supplierStoreElement.dataset.activeCategorySlug.trim() !== "") {
      return supplierStoreElement.dataset.activeCategorySlug;
    }

    return "SELLER";
  }

  // Check if we're on a product category page (check for non-empty data-category-slug)
  const categoryElement = document.querySelector("[data-category-slug]");
  if (categoryElement && categoryElement.dataset.categorySlug && categoryElement.dataset.categorySlug.trim() !== "") {
    return categoryElement.dataset.categorySlug;
  }

  // Check URL for product-category path
  const urlMatch = window.location.pathname.match(/\/product-category\/([^\/]+)/);
  if (urlMatch) {
    return urlMatch[1];
  }

  // Default to SERP for search results
  return "SERP";
}

/**
 * Load user preferences from localStorage
 * @param {string} category - Category slug
 * @returns {Array<string>|null} Array of column keys or null if not found
 */
function loadUserPreferences(category) {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const configs = JSON.parse(stored);
    return configs[category] || null;
  } catch (e) {
    console.warn("[Column Config] Failed to load preferences:", e);
    return null;
  }
}

/**
 * Save user preferences to localStorage
 * @param {string} category - Category slug
 * @param {Array<string>} columnKeys - Array of column keys
 */
function saveUserPreferences(category, columnKeys) {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const configs = stored ? JSON.parse(stored) : {};
    
    configs[category] = columnKeys;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(configs));
  } catch (e) {
    console.warn("[Column Config] Failed to save preferences:", e);
  }
}

export const columnState = {
  /**
   * Get current columns
   * @returns {Array} Current column configuration
   */
  getColumns: () => columns,

  /**
   * Set new columns and notify all subscribers
   * @param {Array} newColumns - New column configuration
   */
  setColumns: (newColumns) => {
    // Only update if columns actually changed
    const newKeys = newColumns.map((c) => c.key).join(",");
    const oldKeys = columns.map((c) => c.key).join(",");

    if (newKeys !== oldKeys) {
      columns = newColumns;
      subscribers.forEach((cb) => cb(columns));
    }
  },

  /**
   * Subscribe to column changes
   * @param {Function} callback - Function to call when columns change
   * @returns {Function} Unsubscribe function
   */
  subscribe: (callback) => {
    subscribers.add(callback);
    return () => subscribers.delete(callback);
  },

  /**
   * Get current category
   * @returns {string} Category slug
   */
  getCurrentCategory,

  /**
   * Load user preferences for a category
   * @param {string} category - Category slug
   * @returns {Array<string>|null} Array of column keys or null
   */
  loadUserPreferences,

  /**
   * Save user preferences for a category
   * @param {string} category - Category slug
   * @param {Array<string>} columnKeys - Array of column keys
   */
  saveUserPreferences,

  /**
   * Get fixed column keys
   * @returns {Object} Object with first and last fixed keys
   */
  getFixedColumns: () => ({
    first: FIXED_FIRST,
    last: FIXED_LAST,
  }),
};
